import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Eye, Target, MessageCircle, Users, Shield, Lightbulb, HeartHandshake } from 'lucide-react';

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.92 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };
const slideLeft = { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };
const slideRight = { hidden: { opacity: 0, x: 60 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

function AnimSection({ children, className = '', variants = stagger, ...props }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });
  return (
    <motion.section ref={ref} variants={variants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className} {...props}>
      {children}
    </motion.section>
  );
}

const teamMembers = [
  { name: 'Dr. Sarah Mitchell', role: 'Founder & Clinical Director', image: 'https://images.unsplash.com/photo-1678695972687-033fa0bdbac9?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { name: 'Dr. James Carter', role: 'Neuropsychologist', image: 'https://images.unsplash.com/photo-1657727534530-fcb5d2002c2a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { name: 'Dr. Priya Patel', role: 'Behavioral Therapist', image: 'https://images.unsplash.com/photo-1643297654397-97b3201abc7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { name: 'Dr. Marcus Robinson', role: 'Child Psychologist', image: 'https://images.unsplash.com/photo-1747715506500-a458cf9ddf92?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
];

export default function AboutUs() {
  return (
    <div className="pt-16 overflow-hidden" data-testid="about-us-page">
      {/* ═══════════ HERO — Full-bleed visual ═══════════ */}
      <section className="relative h-[70vh] flex items-end">
        <motion.img
          src="https://images.unsplash.com/photo-1668875437664-6d53123d3341?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Our diverse medical team"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--text-headline)] via-[var(--text-headline)]/40 to-transparent" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full"
        >
          <p className="uppercase tracking-[0.3em] text-xs font-bold text-[var(--primary-green)] mb-4">About Giving Hope LLC</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-[1.1]">
            Transforming Lives Through<br /><span className="text-[var(--primary-green)]">Compassionate Care</span>
          </h1>
        </motion.div>
      </section>

      {/* ═══════════ ABOUT INTRO — Image-left text-right ═══════════ */}
      <AnimSection className="section-padding" data-testid="about-intro-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div variants={slideLeft} className="relative">
            <div className="absolute -inset-4 bg-[var(--primary-green)]/10 rounded-3xl blur-xl" />
            <img src="https://images.unsplash.com/photo-1774853114355-1ac941f85397?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="Modern wellness office" className="relative rounded-3xl shadow-lg" />
          </motion.div>
          <motion.div variants={slideRight}>
            <p className="text-[var(--text-body)] text-base leading-relaxed mb-6">
              Giving Hope LLC was founded with the mission to empower individuals on their mental health journey, offering guidance to those with a history of substance abuse and mood disorders such as depression, anxiety, PTSD, and ADHD.
            </p>
            <p className="text-[var(--text-body)] text-base leading-relaxed mb-6">
              The clinic was created to help people regain control of their lives in an outpatient setting, reducing the need for hospitalization while providing comprehensive support. Having witnessed the challenges of mental health firsthand and treated numerous patients facing severe diagnoses, we recognized the lack of accessible resources.
            </p>
            <p className="text-[var(--text-body)] text-base leading-relaxed font-semibold">
              Giving Hope LLC was born out of the belief that where there is hope, there is the possibility for change and healing.
            </p>
          </motion.div>
        </div>
      </AnimSection>

      {/* ═══════════ VISION & MISSION — Visual Bento ═══════════ */}
      <AnimSection className="section-padding bg-[var(--bg-alt)]" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Vision */}
            <motion.div variants={scaleIn} className="card-elevated p-8 lg:col-span-2 relative overflow-hidden" data-testid="vision-card">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[var(--primary-green)]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                    <Eye size={22} className="text-[var(--primary-blue)]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold">Our Vision</h2>
                </div>
                <p className="text-[var(--text-body)] text-base leading-relaxed">
                  To be a leading holistic mental health care provider, empowering individuals to achieve lasting well-being through personalized, compassionate support. We aspire to create a community where everyone can thrive and reach their full potential in mental, emotional, and physical health.
                </p>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div variants={scaleIn} className="relative overflow-hidden rounded-2xl" data-testid="mission-card">
              <div className="absolute inset-0 bg-[var(--primary-blue)]" />
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, var(--primary-green) 0%, transparent 50%)' }} />
              <div className="relative p-8 h-full flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                    <Target size={22} className="text-[var(--primary-green)]" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-white">Our Mission</h2>
                </div>
                <p className="text-white/85 text-base leading-relaxed">
                  To provide holistic, compassionate care that treats the whole person. We focus on personalized support, empowering clients to reach their mental health goals and improve their well-being.
                </p>
              </div>
            </motion.div>

            {/* Values */}
            <motion.div variants={fadeUp} className="card-elevated p-8 lg:col-span-3" data-testid="values-card">
              <h2 className="text-2xl font-heading font-bold mb-8">Why Choose Giving Hope LLC?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: Shield, title: 'Specialized Expertise', desc: 'Evidence-based medication management and tailored services for substance use disorders through OP and IOP programs.' },
                  { icon: Users, title: 'Holistic Care for All Ages', desc: 'Outpatient services designed for everyone — from children to adults — ensuring every family member receives care.' },
                  { icon: HeartHandshake, title: 'Women & Family Focus', desc: 'Support services addressing the needs of women and parents, providing a safe space for personal challenges.' },
                  { icon: Lightbulb, title: 'Flexible, Accessible Care', desc: 'In-person and telehealth services to fit your schedule and comfort level.' },
                  { icon: Target, title: 'Personalized Approach', desc: 'Our team creates treatment plans as unique as you are, fostering hope, healing, and long-term well-being.' },
                  { icon: Eye, title: 'Proven Results', desc: 'Clients consistently report significant improvements. We are committed to this tradition of excellence.' },
                ].map((val) => (
                  <motion.div key={val.title} variants={fadeUp} className="p-5 rounded-2xl bg-[var(--bg-alt)] group hover:bg-blue-50 transition-colors duration-300">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-3 group-hover:bg-[var(--primary-blue)] transition-colors">
                      <val.icon size={18} className="text-[var(--primary-blue)] group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="font-heading font-bold mb-1.5 text-sm">{val.title}</h4>
                    <p className="text-xs text-[var(--text-body)] leading-relaxed">{val.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </AnimSection>

      {/* ═══════════ FOUNDER'S MESSAGE — Cinematic ═══════════ */}
      <AnimSection className="relative overflow-hidden" data-testid="founders-message-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1746021513193-a314945d15c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="Peaceful space" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div variants={slideLeft} className="relative">
              <div className="absolute -inset-6 bg-[var(--primary-blue)]/5 rounded-[2rem] blur-xl" />
              <img src="https://images.unsplash.com/photo-1678695972687-033fa0bdbac9?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="Founder" className="relative rounded-3xl shadow-2xl" />
            </motion.div>
            <motion.div variants={slideRight}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <MessageCircle size={22} className="text-[var(--primary-blue)]" />
                </div>
                <h2 className="text-2xl font-heading font-bold">Founder's Message</h2>
              </div>
              <blockquote className="text-[var(--text-body)] text-base leading-relaxed mb-6 border-l-4 border-[var(--primary-green)] pl-6 italic">
                "When I founded Giving Hope LLC, I carried one belief: that every person — no matter their diagnosis — has limitless potential. Having witnessed the challenges of mental health firsthand and treated numerous patients facing severe diagnoses, I recognized the lack of accessible resources. Our team is driven by a passion to uncover that potential through careful assessment, compassionate intervention, and innovative education. We don't just provide clinical services; we plant seeds of hope that grow into lasting change."
              </blockquote>
              <div>
                <p className="font-heading font-bold text-[var(--text-headline)]">Dr. Sarah Mitchell</p>
                <p className="text-sm text-[var(--primary-blue)]">Founder &amp; Clinical Director</p>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimSection>

      {/* ═══════════ TEAM ═══════════ */}
      <AnimSection className="section-padding bg-[var(--bg-alt)]" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[var(--primary-blue)] mb-3">Our Team</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold">Meet the Experts Behind Your Care</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((m, i) => (
              <motion.div key={m.name} variants={scaleIn} className="group" data-testid={`team-member-${i}`}>
                <div className="card-elevated p-0 overflow-hidden">
                  <div className="h-72 overflow-hidden relative">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--text-headline)]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-heading font-bold text-base">{m.name}</h3>
                    <p className="text-sm text-[var(--primary-blue)]">{m.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimSection>

      {/* ═══════════ CTA ═══════════ */}
      <AnimSection className="section-padding bg-[var(--primary-blue)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, var(--primary-green) 0%, transparent 40%)' }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">Join Our Mission of Giving Hope</h2>
            <p className="text-white/80 text-base md:text-lg mb-10">Whether you need clinical services or want to partner with us — we'd love to hear from you.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="btn-green flex items-center gap-2" data-testid="about-contact-btn">Contact Us <ArrowRight size={16} /></Link>
              <Link to="/training" className="bg-white/15 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-white/25 transition-all flex items-center gap-2">View Training Programs</Link>
            </div>
          </motion.div>
        </div>
      </AnimSection>
    </div>
  );
}
